import { useEffect, useRef, useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";
import { Logo, LogoMark } from "./Logo";
import { PHONE_TEL, PHONE_DISPLAY, waLink } from "../constants/images";
import { Phone, Globe, Menu, X } from "lucide-react";

const scrollTo = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const LangSwitcher = () => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const current = LANGS.find((l) => l.code === lang);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="lang-switcher-button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 font-mono2 text-xs tracking-widest border border-white/15 hover:border-[#D96B27] hover:text-[#D96B27] transition-colors duration-300 px-3 py-2 text-[#F4F1EA]"
        aria-label="Change language"
      >
        <Globe className="w-3.5 h-3.5" />
        {current.short}
      </button>
      {open && (
        <div
          data-testid="lang-switcher-menu"
          className="absolute end-0 mt-2 w-44 bg-[#14181D] border border-white/15 backdrop-blur-xl z-50 shadow-2xl"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => {
                setLang(l.code);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors duration-200 ${
                l.code === lang ? "text-[#D96B27] bg-[#D96B27]/10" : "text-[#F4F1EA]/80 hover:text-[#D96B27] hover:bg-white/5"
              }`}
            >
              <span className="font-mono2 text-xs">{l.short}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { hash: "#usluge", label: t.nav.services, id: "services" },
    { hash: "#onama", label: t.nav.about, id: "about" },
    { hash: "#recenzije", label: t.nav.reviews, id: "reviews" },
    { hash: "#kontakt", label: t.nav.contact, id: "contact" },
  ];

  const go = (hash) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    setTimeout(() => scrollTo(hash), menuOpen ? 80 : 0);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0D0F12]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Logo onClick={(e) => { e.preventDefault(); if (window.__lenis) window.__lenis.scrollTo(0); else window.scrollTo(0, 0); }} />

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.hash}
              data-testid={`nav-link-${l.id}`}
              onClick={go(l.hash)}
              className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#F4F1EA]/75 hover:text-[#D96B27] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitcher />
          <a
            data-testid="navbar-phone-link"
            href={PHONE_TEL}
            className="hidden md:flex items-center gap-2 bg-[#D96B27] hover:bg-[#ED7A33] transition-colors duration-300 text-[#0D0F12] font-semibold text-sm px-4 py-2.5"
          >
            <Phone className="w-4 h-4" />
            <span className="font-mono2 tracking-tight">{PHONE_DISPLAY}</span>
          </a>
          <button
            data-testid="navbar-mobile-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden text-[#F4F1EA] p-2"
            aria-label="Menu"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-[#0D0F12]/97 backdrop-blur-xl border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.hash}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={go(l.hash)}
                className="font-display font-bold text-2xl text-[#F4F1EA] hover:text-[#D96B27] transition-colors py-2.5"
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-3 mt-4">
              <a data-testid="mobile-call-link" href={PHONE_TEL} className="flex-1 flex items-center justify-center gap-2 bg-[#D96B27] text-[#0D0F12] font-semibold px-4 py-3">
                <Phone className="w-4 h-4" /> {t.hero.ctaCall}
              </a>
              <a data-testid="mobile-whatsapp-link" href={waLink(t.contact.waMessage)} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 border border-[#25D366] text-[#25D366] font-semibold px-4 py-3">
                {t.hero.ctaWhatsapp}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
